import { cloneDeep } from 'lodash';
import { isNumber } from 'utils/stringUtils';

/**
 * Converts JSON -> object managing bidirectional relationships.
 * See https://www.toptal.com/javascript/bidirectional-relationship-in-json
 * @param obj
 * @returns {*}
 */
export const convertToObject = (json, depth) => {
  // Check if an object is an array
  const isObject = (value) => value && typeof value === 'object';

  // Iterate object properties and store all reference keys and references
  const getKeys = (obj, keyProperty, depth) => {
    let keys = [];
    Object.keys(obj).forEach((key) => {
      // Skip methods
      if (!Object.hasOwn(obj, key)) {
        return;
      }
      if (isObject(obj[key]) && depth >= 0) {
        keys = keys.concat(getKeys(obj[key], keyProperty, depth - 1));
      } else if (key === keyProperty) {
        keys.push({ key: obj[keyProperty], obj });
      }
    });
    return keys;
  };

  const convertToObjectHelper = (json, keyProperty, keys, path, maxDepth) => {
    // Store all reference keys and references to object map
    if (!keys) {
      keys = getKeys(json, keyProperty, maxDepth);

      const convertedKeys = {};
      keys.forEach((elem) => {
        convertedKeys[elem.key] = elem.obj;
      });
      keys = convertedKeys;
    }

    if (!path) {
      path = [];
    }
    if (path.length >= maxDepth) {
      return json;
    }

    const obj = json;

    // Iterate all object properties and object children
    // recursively and replace references with real objects
    Object.keys(obj).forEach((key) => {
      // Skip methods
      if (!Object.hasOwn(obj, key)) {
        return;
      }
      if (!isNumber(key)) path.push(key);
      if (isObject(obj[key])) {
        // Property is an object, so process its children
        // (note well: recursive call)
        convertToObjectHelper(obj[key], keyProperty, keys, path, maxDepth);
      } else if (key === keyProperty) {
        // Remove reference id
        // delete obj[key];
      } else if (keys[obj[key]]) {
        // Replace reference with cloned real object
        obj[key] = cloneDeep(keys[obj[key]]);
      }

      if (!isNumber(key)) path.pop();
    });
    return obj;
  };

  // As discussed above, the serializer needs to use some unique property name for
  // the IDs it generates. Here we use "@id" since presumably prepending the "@" to
  // the property name is adequate to ensure that it is unique. But any unique
  // property name can be used, as long as the same one is used by the serializer
  // and deserializer.
  //
  // Also note that we leave off the 3rd parameter in our call to
  // convertToObjectHelper since it will be initialized within that function if it
  // is not provided.
  return convertToObjectHelper(
    json ? cloneDeep(json) : {},
    '@id',
    null,
    null,
    depth > 0 ? depth : 3
  );
};

/**
 * Converts JSON (JSOG) -> object managing bidirectional relationships.
 * See https://github.com/jsog/jsog
 * @returns {*}
 * @param encoded
 * @param depth
 * @param idProperty
 * @param refProperty
 */
export const decodeJSOG = (encoded, depth, idProperty, refProperty) => {
  if (depth == null) {
    depth = 3;
  }
  if (idProperty == null) {
    idProperty = '@id';
  }
  if (refProperty == null) {
    refProperty = '@ref';
  }

  const found = {};

  const doDecode = (encoded, curDepth) => {
    const decodeObject = (encoded) => {
      let ref = encoded[refProperty];
      if (ref != null) {
        ref = ref.toString();
      }
      if (ref != null) {
        return found[ref];
      }
      const result = {};
      let id = encoded[idProperty];
      if (id != null) {
        id = id.toString();
      }
      if (id) {
        found[id] = result;
      }

      Object.entries(encoded).forEach(([key, value]) => {
        if (key !== idProperty) {
          result[key] = doDecode(value, curDepth - 1);
        }
      });

      return result;
    };

    const decodeArray = (encoded) =>
      (() => {
        const results = [];

        Object.values(encoded).forEach((value) => {
          results.push(doDecode(value, curDepth - 1));
        });

        return results;
      })();
    if (encoded == null || curDepth <= 0) {
      return encoded;
    }
    if (Array.isArray(encoded)) {
      return decodeArray(encoded);
    }
    if (typeof encoded === 'object') {
      return decodeObject(encoded);
    }
    return encoded;
  };

  return doDecode(encoded, depth);
};
